import React, { useState, useRef } from 'react';
import { ChevronDown, Building2, Check, MapPin, Loader2, Shield, LogOut, Key } from 'lucide-react';
import { useWorkspace, permissionCategories } from '../contexts/WorkspaceContext';
import { ConfirmModal } from './ConfirmModal';
export function Header() {
  const {
    currentWorkspace,
    currentUser,
    logout,
    loading,
    workspaces,
    setCurrentWorkspace,
    setCurrentSubRegion,
    currentSubRegion,
    getUserPermissions
  } = useWorkspace();
  const [isUserOpen, setIsUserOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const [isTeamOpen, setIsTeamOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const primaryColor = currentWorkspace.primaryColor || '#2563eb';
  if (loading) {
    return <header className="h-16 border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-8 flex items-center flex-shrink-0" data-id="element-2051">
        <Loader2 className="h-5 w-5 animate-spin mr-3" style={{
        color: primaryColor
      }} data-id="element-2052" />
        <span className="text-sm text-gray-500" data-id="element-2053">Loading workspace...</span>
      </header>;
  }
  const roleBadge = currentUser.Role === 'Super Admin' ? 'bg-purple-50 text-purple-700 ring-purple-700/10' : currentUser.Role === 'Team Manager' ? 'bg-blue-50 text-blue-700 ring-blue-700/10' : 'bg-gray-50 text-gray-700 ring-gray-700/10';
  // Get user's permissions grouped by category
  const userPermissions = getUserPermissions();
  const permissionsByCategory = Object.entries(permissionCategories).map(([category, perms]) => {
    const granted = perms.filter(p => userPermissions.includes(p as any));
    return {
      category,
      total: perms.length,
      granted: granted.length,
      permissions: granted
    };
  }).filter(c => c.granted > 0);
  return <header className="h-16 border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-shrink-0" data-id="element-2054">
      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1" data-id="element-2055">
        <span className="text-sm text-gray-500 hidden sm:inline flex-shrink-0" data-id="element-2056">
          Scope:
        </span>

        {/* Team Selector */}
        <div className="relative flex-shrink-0" data-id="element-2057">
          <button onClick={() => setIsTeamOpen(!isTeamOpen)} className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-sm font-medium text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors whitespace-nowrap" data-id="element-2058">
            <Building2 className="h-4 w-4 text-gray-500 flex-shrink-0" data-id="element-2059" />
            <span className="truncate max-w-[100px] sm:max-w-[150px]" data-id="element-2060">
              {currentWorkspace.name}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isTeamOpen ? 'rotate-180' : ''}`} data-id="element-2061" />
          </button>

          {isTeamOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setIsTeamOpen(false)} data-id="element-2062" />
              <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20 max-h-80 overflow-y-auto" data-id="element-2063">
                {workspaces.length === 0 ? <div className="px-4 py-3 text-sm text-gray-500 italic" data-id="element-2064">
                    No teams available. Go to{' '}
                    <a href="/regions" className="text-blue-600 underline" data-id="element-2065">
                      Regions
                    </a>{' '}
                    to set up your first team.
                  </div> : workspaces.map(team => <button key={team.id} onClick={() => {
              setCurrentWorkspace(team);
              setCurrentSubRegion(null);
              setIsTeamOpen(false);
            }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors" data-id="element-2066">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2067">
                        <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-2068" />
                        <div className="text-left min-w-0" data-id="element-2069">
                          <div className="font-medium text-gray-900 truncate" data-id="element-2070">
                            {team.name}
                          </div>
                          <div className="text-xs text-gray-500 truncate" data-id="element-2071">
                            {team.subRegions.length} region
                            {team.subRegions.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      {currentWorkspace.id === team.id && <Check className="h-4 w-4 flex-shrink-0" style={{
                color: primaryColor
              }} data-id="element-2072" />}
                    </button>)}
              </div>
            </>}
        </div>

        <span className="text-gray-300 flex-shrink-0" data-id="element-2073">/</span>

        {/* Subregion Selector */}
        <div className="relative flex-shrink-0" data-id="element-2074">
          <button onClick={() => setIsRegionOpen(!isRegionOpen)} className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-sm font-medium text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors whitespace-nowrap" data-id="element-2075">
            <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" data-id="element-2076" />
            <span className="truncate max-w-[80px] sm:max-w-[120px]" data-id="element-2077">
              {currentSubRegion || 'All Regions'}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isRegionOpen ? 'rotate-180' : ''}`} data-id="element-2078" />
          </button>

          {isRegionOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setIsRegionOpen(false)} data-id="element-2079" />
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20 max-h-80 overflow-y-auto" data-id="element-2080">
                <button onClick={() => {
              setCurrentSubRegion(null);
              setIsRegionOpen(false);
            }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors" data-id="element-2081">
                  <span className="font-medium text-gray-900" data-id="element-2082">All Regions</span>
                  {!currentSubRegion && <Check className="h-4 w-4 flex-shrink-0" style={{
                color: primaryColor
              }} data-id="element-2083" />}
                </button>
                {currentWorkspace.subRegions.length > 0 && <div className="border-t border-gray-100 my-1" data-id="element-2084" />}
                {currentWorkspace.subRegions.map(region => <button key={region} onClick={() => {
              setCurrentSubRegion(region);
              setIsRegionOpen(false);
            }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors" data-id="element-2085">
                    <span className="font-medium text-gray-900 truncate" data-id="element-2086">
                      {region}
                    </span>
                    {currentSubRegion === region && <Check className="h-4 w-4 flex-shrink-0" style={{
                color: primaryColor
              }} data-id="element-2087" />}
                  </button>)}
                {currentWorkspace.subRegions.length === 0 && <div className="px-4 py-2 text-xs text-gray-500 italic" data-id="element-2088">
                    No regions defined for this team
                  </div>}
              </div>
            </>}
        </div>
      </div>

      {/* User info + permissions dropdown */}
      <div className="relative flex items-center gap-3 flex-shrink-0 ml-4" data-id="element-2089">
        <button onClick={() => setIsUserOpen(!isUserOpen)} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 transition-colors" data-id="element-2090">
          <span className="hidden sm:inline text-sm text-gray-500 truncate max-w-[150px]" data-id="element-2091">
            {currentUser.User_Name}
          </span>
          <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${roleBadge}`} data-id="element-2092">
            {currentUser.Role}
          </span>
          <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${isUserOpen ? 'rotate-180' : ''}`} data-id="element-2093" />
        </button>

        {isUserOpen && <>
            <div className="fixed inset-0 z-10" onClick={() => setIsUserOpen(false)} data-id="element-2094" />
            <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden" data-id="element-2095">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-gray-100" style={{
            backgroundColor: 'var(--primary-50, #eff6ff)'
          }} data-id="element-2096">
                <div className="flex items-center gap-3" data-id="element-2097">
                  <div className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0" style={{
                backgroundColor: primaryColor
              }} data-id="element-2098">
                    {currentUser.User_Name?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0" data-id="element-2099">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2100">
                      {currentUser.User_Name}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-2101">
                      {currentUser.Email}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2" data-id="element-2102">
                  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${roleBadge}`} data-id="element-2103">
                    {currentUser.Role}
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-2104">
                    Team: {currentUser.Team}
                  </span>
                </div>
              </div>

              {/* Permissions summary */}
              <div className="px-4 py-3 max-h-60 overflow-y-auto" data-id="element-2105">
                <div className="flex items-center gap-1.5 mb-2" data-id="element-2106">
                  <Key className="h-3.5 w-3.5 text-gray-400" data-id="element-2107" />
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2108">
                    Permissions
                  </span>
                </div>
                {currentUser.Role === 'Super Admin' ? <div className="flex items-center gap-2 py-1.5" data-id="element-2109">
                    <Shield className="h-4 w-4 flex-shrink-0" style={{
                color: primaryColor
              }} data-id="element-2110" />
                    <span className="text-sm text-gray-700" data-id="element-2111">
                      Full access — all permissions granted
                    </span>
                  </div> : permissionsByCategory.length === 0 ? <p className="text-sm text-gray-500 py-1" data-id="element-2112">
                    No permissions assigned
                  </p> : <div className="space-y-1.5" data-id="element-2113">
                    {permissionsByCategory.map(({
                category,
                total,
                granted
              }) => <div key={category} className="flex items-center justify-between py-1" data-id="element-2114">
                          <span className="text-sm text-gray-700" data-id="element-2115">
                            {category}
                          </span>
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{
                  backgroundColor: granted === total ? 'var(--primary-50, #eff6ff)' : '#f9fafb',
                  color: granted === total ? 'var(--primary-700, #1d4ed8)' : '#6b7280'
                }} data-id="element-2116">
                            {granted}/{total}
                          </span>
                        </div>)}
                  </div>}
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 px-4 py-2" data-id="element-2117">
                <button onClick={() => {
              setIsUserOpen(false);
              setShowSignOutConfirm(true);
            }} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-2118">
                  <LogOut className="h-4 w-4" data-id="element-2119" />
                  Sign out
                </button>
              </div>
            </div>
          </>}
      </div>

      <ConfirmModal isOpen={showSignOutConfirm} title="Sign Out" message="Sign out of ConfigConsole?" confirmLabel="Sign Out" variant="danger" onCancel={() => setShowSignOutConfirm(false)} onConfirm={() => {
      setShowSignOutConfirm(false);
      logout();
    }} data-id="element-2120" />
    </header>;
}